import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AddLiveTapsBL } from '../../../utilities/BL/CarreraContinua';
import {
    ConnectWebSocketForCarreraContinuaFan,
    DisconnectWebSocketForCarreraContinuaFan
} from '../../../utilities/WS/CarreraContinuaFanWS';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    resolveAssetUrl,
    DEFAULT_FAN_BG_SVG,
    DEFAULT_FAN_TAP_SVG
} from '../../Screen/Sections/CarreraContinua/CarreraContinuaAssets';

/**
 * Normaliza y obtiene el nombre amigable de la zona a partir de la configuración o identificador.
 */
const getZoneDisplayName = (zoneId, runners = []) => {
    if (!zoneId) return 'Sin Asignar';
    const safeId = String(zoneId).trim().toLowerCase();

    if (Array.isArray(runners) && runners.length > 0) {
        const found = runners.find(r => String(r.zoneId || r.zoneIdentifier || '').trim().toLowerCase() === safeId);
        if (found && (found.zoneName || found.displayName || found.label)) {
            return found.zoneName || found.displayName || found.label;
        }
    }

    if (safeId.startsWith('zona')) {
        const num = safeId.replace('zona', '').trim();
        return `Zona ${num}`;
    }

    return zoneId.charAt(0).toUpperCase() + zoneId.slice(1);
};

/**
 * COMPONENTE PWA MÓVIL DEL AFICIONADO: CARRERA CONTINUA (HU-05)
 * Maquetación full-screen en "/" con tap ultra-rápido, micro-buffer y wakeLock
 */
export const CarreraContinuaFan = ({
    matchState: propMatchState,
    config: propConfig,
    winnerZone: propWinnerZone,
    userZone: propUserZone,
    roomId: propRoomId
}) => {
    // 1. Detección segura de parámetros de sala y zona
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const activeRoomId = propRoomId || searchParams.get('roomId') || propMatchState?.roomId || localStorage.getItem('active_room_id') || '';
    
    // Zona del usuario (prop > localStorage > sessionStorage > fallback)
    const [userZone, setUserZone] = useState(() => {
        return propUserZone || localStorage.getItem('user_zone') || sessionStorage.getItem('user_zone') || '';
    });

    useEffect(() => {
        if (propUserZone && propUserZone !== userZone) {
            setUserZone(propUserZone);
        }
    }, [propUserZone]);

    // 2. Estados de la partida y fases
    const [matchPhase, setMatchPhase] = useState(() => {
        const p = propMatchState?.phase || propConfig?.phase || 'WAITING';
        return String(p).toUpperCase();
    });

    const [countdownSeconds, setCountdownSeconds] = useState(3);
    const [winnerZone, setWinnerZone] = useState(() => propWinnerZone || null);
    const [themeConfig, setThemeConfig] = useState(() => propConfig || {});
    const [, setConnectionStatus] = useState('connected');

    // Sincronización continua de configuración y estado desde props
    useEffect(() => {
        if (propConfig && typeof propConfig === 'object') {
            setThemeConfig(prev => ({ ...prev, ...propConfig }));
        }
    }, [propConfig]);

    useEffect(() => {
        if (propMatchState) {
            if (propMatchState.phase) {
                setMatchPhase(String(propMatchState.phase).toUpperCase());
            }
            if (propMatchState.theme && typeof propMatchState.theme === 'object') {
                setThemeConfig(prev => ({ ...prev, ...propMatchState.theme }));
            }
            if (propMatchState.winnerZone !== undefined || propMatchState.winner !== undefined) {
                setWinnerZone(propMatchState.winnerZone || propMatchState.winner || null);
            }
        }
    }, [propMatchState]);

    useEffect(() => {
        if (propWinnerZone !== undefined) {
            setWinnerZone(propWinnerZone);
            if (propWinnerZone) {
                setMatchPhase('FINISHED');
            }
        }
    }, [propWinnerZone]);

    // Referencias para WakeLock y Micro-Buffer
    const wakeLockRef = useRef(null);
    const memoryBufferReference = useRef({
        tapsAccumulated: 0,
        dispatchTimerId: null,
        isCurrentlySending: false
    });

    const tapsBatchSize = themeConfig?.tapsPorTick || 10;
    const minimumFlushTimeMilliseconds = themeConfig?.minUpdateMs || 150;

    // =========================================================================
    // 3. SCREEN WAKELOCK: PANTALLA 100% ENCENDIDA Y ACTIVA
    // =========================================================================
    const requestWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator && !wakeLockRef.current) {
                const lock = await navigator.wakeLock.request('screen');
                wakeLockRef.current = lock;
                lock.addEventListener('release', () => {
                    wakeLockRef.current = null;
                });
            }
        } catch (err) {
            // Ignorar si el navegador no permite wakeLock
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        try {
            if (wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (err) {
            // Ignorar
        }
    }, []);

    useEffect(() => {
        requestWakeLock();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLock();
        };
    }, [requestWakeLock, releaseWakeLock]);

    // =========================================================================
    // 4. MICRO-BUFFER INTELIGENTE (BATCHING) PARA REGISTRO DE TAPS
    // =========================================================================
    const flushTapsToBackendAPI = useCallback(async () => {
        const memoryBuffer = memoryBufferReference.current;

        if (memoryBuffer.dispatchTimerId) {
            clearTimeout(memoryBuffer.dispatchTimerId);
            memoryBuffer.dispatchTimerId = null;
        }

        if (memoryBuffer.isCurrentlySending || memoryBuffer.tapsAccumulated <= 0 || !activeRoomId || !userZone) {
            return;
        }

        const tapsToDispatch = memoryBuffer.tapsAccumulated;
        memoryBuffer.tapsAccumulated = 0;
        memoryBuffer.isCurrentlySending = true;

        try {
            await AddLiveTapsBL({
                roomId: activeRoomId,
                zoneIdentifier: userZone,
                tapsCount: tapsToDispatch
            });
        } catch (error) {
            memoryBuffer.tapsAccumulated += tapsToDispatch;
        } finally {
            memoryBuffer.isCurrentlySending = false;
            if (memoryBuffer.tapsAccumulated > 0 && !memoryBuffer.dispatchTimerId) {
                memoryBuffer.dispatchTimerId = setTimeout(() => {
                    memoryBuffer.dispatchTimerId = null;
                    flushTapsToBackendAPI();
                }, minimumFlushTimeMilliseconds);
            }
        }
    }, [activeRoomId, userZone, minimumFlushTimeMilliseconds]);

    useEffect(() => {
        if (matchPhase === 'FINISHED') {
            flushTapsToBackendAPI();
        }
        return () => {
            flushTapsToBackendAPI();
        };
    }, [matchPhase, flushTapsToBackendAPI]);

    // =========================================================================
    // 5. MANEJADOR TÁCTIL DE ALTA VELOCIDAD Y FEEDBACK HÁPTICO
    // =========================================================================
    const isPlayerAllowedToTap = Boolean(
        userZone &&
        activeRoomId &&
        matchPhase === 'RUNNING' &&
        !winnerZone
    );

    const handleScreenTap = () => {
        if (!isPlayerAllowedToTap) return;

        if (navigator.vibrate) {
            try {
                navigator.vibrate(15);
            } catch (err) {}
        }

        const memoryBuffer = memoryBufferReference.current;
        memoryBuffer.tapsAccumulated += 1;

        if (memoryBuffer.tapsAccumulated >= tapsBatchSize) {
            if (memoryBuffer.dispatchTimerId) {
                clearTimeout(memoryBuffer.dispatchTimerId);
                memoryBuffer.dispatchTimerId = null;
            }
            flushTapsToBackendAPI();
            return;
        }

        if (!memoryBuffer.dispatchTimerId) {
            memoryBuffer.dispatchTimerId = setTimeout(() => {
                memoryBuffer.dispatchTimerId = null;
                flushTapsToBackendAPI();
            }, minimumFlushTimeMilliseconds);
        }
    };

    // =========================================================================
    // 6. SINCRONIZACIÓN DE MENSAJES SIGNALR EN TIEMPO REAL
    // =========================================================================
    const processIncomingSignalRMessage = useCallback((envelope) => {
        if (!envelope || !envelope.type) return;

        const type = envelope.type.toLowerCase();
        const payload = envelope.payload !== undefined ? envelope.payload : envelope.data;

        switch (type) {
            case 'carreracontinua.created':
            case 'carreracontinua.create':
            case 'carreracontinua.cover':
            case 'carreracontinua.show_cover':
            case 'carreracontinua.showcover':
            case 'carreracontinua.project_game':
            case 'carreracontinua.game':
            case 'carreracontinua.reset': {
                setMatchPhase('WAITING');
                setWinnerZone(null);
                const themeData = payload?.theme || payload?.data || payload;
                if (themeData && typeof themeData === 'object') {
                    setThemeConfig(prev => ({ ...prev, ...themeData }));
                }
                break;
            }

            case 'carreracontinua.countdown':
                setMatchPhase('COUNTDOWN');
                if (payload && payload.secondsRemaining !== undefined) {
                    setCountdownSeconds(payload.secondsRemaining);
                    if (navigator.vibrate) {
                        try {
                            navigator.vibrate(50);
                        } catch (err) {}
                    }
                }
                break;

            case 'carreracontinua.started':
                setMatchPhase('RUNNING');
                setWinnerZone(null);
                break;

            case 'carreracontinua.progress':
                if (payload && payload.phase && payload.phase.toUpperCase() === 'RUNNING') {
                    setMatchPhase('RUNNING');
                }
                break;

            case 'carreracontinua.paused':
                setMatchPhase('PAUSED');
                break;

            case 'carreracontinua.resumed':
                setMatchPhase('RUNNING');
                break;

            case 'carreracontinua.finished':
                setMatchPhase('FINISHED');
                if (payload) {
                    setWinnerZone(payload.winnerZone || payload.winner || null);
                }
                flushTapsToBackendAPI();
                break;

            case 'assignedzone':
                if (payload && payload.zone) {
                    setUserZone(payload.zone);
                    localStorage.setItem('user_zone', payload.zone);
                }
                break;

            default:
                break;
        }
    }, [flushTapsToBackendAPI]);

    useEffect(() => {
        const handleGenericSignalRMessage = (msg) => {
            processIncomingSignalRMessage(msg);
        };

        SignalRService.on('message', handleGenericSignalRMessage);

        if (!SignalRService.isConnected) {
            ConnectWebSocketForCarreraContinuaFan(
                processIncomingSignalRMessage,
                setConnectionStatus,
                activeRoomId,
                userZone
            );
        }

        return () => {
            SignalRService.off('message', handleGenericSignalRMessage);
        };
    }, [activeRoomId, userZone, processIncomingSignalRMessage]);

    // =========================================================================
    // 7. CÁLCULO VISUAL DE TEXTOS Y GANADORES
    // =========================================================================
    const runnersList = themeConfig?.runners || propMatchState?.runners || [];
    const userZoneName = getZoneDisplayName(userZone, runnersList);

    const isUserZoneWinner = useMemo(() => {
        if (!winnerZone || !userZone) return false;
        const safeWinner = String(winnerZone).trim().toLowerCase();
        const safeUser = String(userZone).trim().toLowerCase();
        return safeWinner === safeUser;
    }, [winnerZone, userZone]);

    const renderWinnerText = () => {
        if (!winnerZone || winnerZone === 'tie') {
            return '¡Ha habido un empate!';
        }
        if (winnerZone.startsWith('tie_partial:')) {
            const tiedZones = winnerZone.split(':')[1].split(' y ');
            const names = [...new Set(tiedZones.map(z => getZoneDisplayName(z, runnersList)))];
            return `¡Empataron ${names.join(' y ')}!`;
        }
        if (isUserZoneWinner) {
            return '¡Has ganado!';
        }
        const winningName = getZoneDisplayName(winnerZone, runnersList);
        return `¡Ha ganado la zona ${winningName}!`;
    };

    // Fondo visual dinámico y botón dedicado de Carrera Continua
    const rawBg = themeConfig?.fanBackgroundUrl || themeConfig?.coverBackgroundUrl || null;
    const fanBgImage = resolveAssetUrl(rawBg) || DEFAULT_FAN_BG_SVG;

    const rawTap = themeConfig?.fanTapButtonUrl || themeConfig?.tapButtonUrl || null;
    const fanTapImage = resolveAssetUrl(rawTap) || DEFAULT_FAN_TAP_SVG;

    return (
        <div className="gfan ccfan">
            {/* Fondo fijado a pantalla completa */}
            <div className="gfan__background">
                <img src={fanBgImage} className="gfan__background__Image" alt="Fondo Carrera" />
            </div>

            {/* Cabecera / Identidad del Equipo */}
            <header className="container Fan__Goal__GameHeader">
                <div className="w-100 d-flex justify-content-center">
                    <div className="text-end mt-3 me-3">
                        <p className="gfan__controls__who mb-0 text-white font-weight-bold text-center" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            TU PERSONAJE / EQUIPO:
                        </p>
                        <h4 className="text-uppercase text-danger fw-bold text-center" style={{ textShadow: '2px 2px 4px rgba(255,255,255,0.9)' }}>
                            {userZoneName || 'No Asignada'}
                        </h4>
                    </div>
                </div>
            </header>

            {/* Controles centrales y botón de Tap */}
            <div className="gfan__controls">
                {matchPhase === 'FINISHED' && (
                    <p className="gfan__controls__tap__Text" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                        {renderWinnerText()}
                    </p>
                )}

                {matchPhase === 'COUNTDOWN' && !winnerZone && (
                    <p className="gfan__controls__tap__Text" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                        {countdownSeconds > 0 ? `¡En sus marcas... ${countdownSeconds}!` : '¡A CORRER!'}
                    </p>
                )}

                {matchPhase !== 'FINISHED' && matchPhase !== 'COUNTDOWN' && (
                    <p className="gfan__controls__tap__Text" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                        {isPlayerAllowedToTap ? '¡Corre! Haz tap en el botón' : '¡Prepárate para correr!'}
                    </p>
                )}

                <button
                    className={`gfan__controls__tap ${isPlayerAllowedToTap ? '' : 'gfan__controls__tap--disabled'}`}
                    onClick={handleScreenTap}
                    onTouchStart={handleScreenTap}
                    type="button"
                    aria-label="Botón de carrera"
                >
                    <img
                        src={fanTapImage}
                        className="gfan__controls__tap__image"
                        alt="Botón Tap"
                        draggable="false"
                    />
                </button>
            </div>

            {/* Créditos de pie de página */}
            <div className="Fan__Goal__Footer">
                <p className="FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
            </div>
        </div>
    );
};

export default CarreraContinuaFan;
